import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Deceased Client → Deceased Client.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Deceased Client(s) Screen" and
 * "Deceased Client Files Screen". The Block All / Block Some answer decides
 * whether the accounts table appears and how the case description is built.
 * Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Deceased Client',
    subCategory: 'Deceased Client',
    subject: subjectRow('Deceased client - 0989-022356'),
    lookups: [['Household', 'The deceased client’s household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
  }),
  {
    title: 'Answer the Deceased Client(s) Details questions',
    body: (
      <p style={{ margin: 0 }}>
        Questions reveal themselves one at a time based on your answers, so you only see what applies. Every
        question in the reference below is required once it appears. The date of death is saved to the case.
      </p>
    ),
  },
  {
    title: 'Write the instructions, and choose the accounts if blocking some',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Additional Instructions</strong> box becomes the case description Operations reads first. If you
          chose to block all accounts, the description starts with <em>BLOCK ALL ACCOUNTS (ENTIRE HOUSEHOLD)</em>{' '}
          followed by what you write.
        </p>
        <p style={{ margin: 0 }}>
          If you chose <em>Block Some Accounts</em>, the table <strong>Deceased Client Accounts</strong> appears once
          the instructions box has something in it. Tick every account that belonged to the deceased client; you can
          pick several. It lists the household&rsquo;s active accounts with Account Number, Account Details, Total Cash
          and Total Value. If you chose <em>Block All Accounts</em> there is no table - the whole household is covered.
        </p>
      </>
    ),
  },
  {
    title: 'Attach the paperwork, acknowledge and submit',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Deceased Client Files</strong> screen says: <em>Please add any related Documents to the tiles
          below. Please note, missing or incomplete documents may result in a NIGO.</em> Add the death certificate and
          any other paperwork you have.
        </p>
        <p style={{ margin: 0 }}>
          Read the Operations and Trading notice below the upload. Select <em>I have read, understand, and agree to the
          above information</em> under Acknowledgement and click Next. FSC creates the case and opens it. Note the case
          number for follow-up.
        </p>
      </>
    ),
  },
];

// Every question on the Deceased Client(s) Screen, in the order it can appear.
const QUESTIONS = [
  {
    q: 'Date of Death',
    when: 'Always - this is the first question',
    note: 'Saved to the case as its event date.',
  },
  {
    q: 'Do you have the death certificate and/or any additional paperwork?',
    when: 'Once the date of death is entered',
    options: ['Yes (I have paperwork)', 'No (I do not have paperwork)'],
    note: 'The form lets you continue either way. If you answer Yes, attach the paperwork on the next screen.',
  },
  {
    q: 'Block Some Accounts or All Accounts? (Is there a surviving spouse?)',
    when: 'Once the paperwork question is answered',
    options: ['Block All Accounts (Mark Entire Household as Deceased)', 'Block Some Accounts (Not All Accounts belong to the Deceased Client)'],
    note: (
      <>
        The screen explains: &ldquo;If the deceased client is survived by a Spouse, it may not be necessary to close
        all accounts. If the client was not married / Is not survived by anyone, select &ldquo;Close All
        Accounts&rdquo;. Otherwise, if there are accounts in the name of the surviving Spouse ONLY, select &ldquo;Close
        Some Accounts&rdquo; then select the specific accounts that will need to be closed.&rdquo; Those refer to the{' '}
        <em>Block All Accounts</em> and <em>Block Some Accounts</em> options.
      </>
    ),
  },
  {
    q: 'Additional Instructions',
    when: 'Once Block All or Block Some is chosen',
    note: 'Becomes the case description. With Block Some, the accounts table appears once this has something in it.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Deceased Client'),
  {
    h: "The Deceased Client Accounts table hasn't appeared",
    p: ['It only appears if you chose Block Some Accounts, and only once the Additional Instructions box has something in it. With Block All Accounts there is no table.'],
  },
  {
    h: "The account I expected isn't in the list",
    p: ['The table lists the household’s active accounts (not sleeves). An inactive account, or one under a different household, will not appear.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function DeceasedClient() {
  return (
    <GuidePage
      title="Deceased Client Requests"
      icon="person-x"
      path="Operations → Deceased Client → Deceased Client"
      minutes={4}
      intro="How to notify the Prosperity Operations team of a client's death - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form is short, but the account choice matters.</Para>
        <Bullets items={[
          <><strong>The date of death.</strong></>,
          <><strong>The death certificate</strong> and any other paperwork you have, scanned. You upload them on the last screen.</>,
          <><strong>Whether there is a surviving spouse</strong>, and which of the household&rsquo;s accounts belonged to the deceased client. You pick them from a list, so you do not need account numbers memorised.</>,
        ]} />
        <Callout>
          Choose <em>Block All Accounts (Mark Entire Household as Deceased)</em> only when every account in the household
          belonged to the deceased client. If the surviving spouse has accounts in their own name, choose{' '}
          <em>Block Some Accounts</em> and pick the deceased client&rsquo;s accounts.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not estate or legal advice, and it does not describe how the
        accounts are retitled or distributed after the case is opened.
      </Footnote>
    </GuidePage>
  );
}
